import { KeyboardEvent, RefObject, useEffect, useRef, useState } from "react"
import AuditLogService, { IAuditLog } from "../../services/AuditLogService"
import Configs from "../../Configs"
import DeleteConfirmation from "../../components/DeleteConfirmation"
import ContentHeader from "../../components/ContentHeader"
import ContentBody from "../../components/ContentBody"
import { Alert, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Table } from "react-bootstrap"
import Pagination from "../../components/Pagination"
import Utils from "../../utils/Utils"
import Confirmation from "../../components/Confirmation"
import useAuthContext from "../../hooks/useAuthContext"
import Permissions from "../../constants/Permissions"

function AuditLogs() {
    const { hasPermission } = useAuthContext()

    const [tableData, setTableData] = useState({
        data: new Array<IAuditLog>(),
        hasNext: false,
        hasPrevious: false,
        pageIndex: 1,
        pageSize: Configs.DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0
    })

    const keywordRef = useRef<HTMLInputElement>(null)
    const [message, setMessage] = useState("")
    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [deletedItem, setDeletedItem] = useState({
        id: 0,
        name: ""
    })

    const FilterFn = async (pageIndex: number) => {
        let pageSize = Configs.DEFAULT_PAGE_SIZE
        let keyword = keywordRef.current?.value || ""

        const res = await AuditLogService.filter(pageIndex, pageSize, keyword)

        if (res?.isSuccess) {
            setTableData(res.data)
        } else {
            console.log(res?.message)
        }

        setCurrentPageIndex(pageIndex)
    }

    useEffect(() => {
        keywordRef.current?.focus()

        FilterFn(1)
    }, [])

    const handleSearch = () => {
        FilterFn(1)
    }

    const handleSearchInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            FilterFn(1)
        }
    }

    const handlePageChange = (pageNumber: number) => {
        FilterFn(pageNumber)
    }

    //Delete
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false)

    const hideConfirmationModal = () => {
        setDeletedItem({
            id: 0,
            name: ""
        })

        setDisplayConfirmationModal(false)
    }

    const showDeleteConfirmation = (id: number | null, name: string) => {
        setDeletedItem({
            id: id || 0,
            name: name
        })

        setDisplayConfirmationModal(true)
        setMessage("")
    }

    const handleDelete = async () => {
        if (deletedItem.id > 0) {
            const res = await AuditLogService.delete(deletedItem.id)

            if (res?.isSuccess) {
                FilterFn(currentPageIndex)
            }

            hideConfirmationModal()
            setMessage(res.message)
        }
    }

    //Clear
    const [displayClearConfirmationModal, setDisplayClearConfirmationModal] = useState(false)

    const hideClearConfirmationModal = () => {
        setDisplayClearConfirmationModal(false)
    }

    const showClearConfirmation = () => {
        setDisplayClearConfirmationModal(true)
        setMessage("")
    }

    const handleClear = async () => {
        const res = await AuditLogService.clear()

        if (res?.isSuccess) {
            FilterFn(1)
        }

        hideClearConfirmationModal()
        setMessage(res.message)
    }

    //View Log Details
    const [formData, setFormData] = useState<IAuditLog | null>(null)

    const handleView = async (id: number) => {
        var item = tableData.data.find(x => x.id === id) || null

        if (item) {
            setFormData(item)
            handleShowModal()
        }
    }

    const [showModal, setShowModal] = useState(false)
    const handleCloseModal = () => {
        setShowModal(false)
    }
    const handleShowModal = () => {
        setShowModal(true)
        setMessage("")
    }

    const showTableDetails = () => {
        var elements: any[] = []

        if (formData) {
            var affectedColumns = Utils.isJSON(formData.affectedColumns) ? JSON.parse(formData.affectedColumns) : null
            var oldValues = Utils.isJSON(formData.oldValues) ? JSON.parse(formData.oldValues) : null
            var newValues = Utils.isJSON(formData.newValues) ? JSON.parse(formData.newValues) : null

            affectedColumns?.forEach((col: string, index: number) => {
                var oldVal = oldValues[col]?.toString()
                var newVal = newValues[col]?.toString()

                elements.push(<tr key={index}>
                    <td>{col}</td>
                    <td>{oldVal}</td>
                    <td className={oldVal !== newVal ? "text-danger" : ""}>{newVal}</td>
                </tr>)
            })
        }

        return elements
    }

    return (
        <>
            <ContentHeader title="Audit Logs" />

            <ContentBody>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Header>
                                    {hasPermission(Permissions.ApiLogs.Clear) &&
                                        <Button variant="default" size="sm" onClick={showClearConfirmation}>
                                            <i className="fas fa-trash-alt"></i> Clear logs
                                        </Button>
                                    }

                                    <div className="card-tools">
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="search"
                                                placeholder="Search"
                                                ref={keywordRef as RefObject<HTMLInputElement>}
                                                onKeyUp={handleSearchInputKeyPress}
                                            />
                                            <Button variant="info" size="sm" onClick={handleSearch}>
                                                <i className="fas fa-search"></i>
                                            </Button>
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {message !== "" && <Alert variant="info" onClose={() => setMessage("")} dismissible>{message}</Alert>}
                                    <Table responsive striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Changed On</th>
                                                <th>Audit Type</th>
                                                <th>Table Name</th>
                                                <th>Primary Key</th>
                                                <th>Changed By</th>
                                                <th style={{ textAlign: "center", width: "80px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                tableData.data.map(item =>
                                                    <tr key={item.id}>
                                                        <td>{Utils.dateToString(item.changedOn, Configs.DATE_TIME_FORMAT)}</td>
                                                        <td>{item.auditType || ""}</td>
                                                        <td>{item.tableName || ""}</td>
                                                        <td>{item.primaryKey || ""}</td>
                                                        <td>{item?.user?.userName || ""}</td>
                                                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                            <Button variant="warning" className="mr-1" size="sm" onClick={() => handleView(item.id)}><i className="fas fa-eye"></i> View</Button>
                                                            {hasPermission(Permissions.AuditLogs.Delete) &&
                                                                <Button variant="danger" size="sm" onClick={() => showDeleteConfirmation(item.id, item.tableName)}><i className="fas fa-trash-alt"></i> Delete</Button>
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer>
                                    <Pagination hasNext={tableData.hasNext} hasPrevious={tableData.hasPrevious} pageIndex={tableData.pageIndex} pageSize={tableData.pageSize} totalPages={tableData.totalPages} totalRecords={tableData.totalRecords} handlePageChange={handlePageChange} />
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </ContentBody>

            <DeleteConfirmation show={displayConfirmationModal} onConfirm={handleDelete} onHide={hideConfirmationModal} id={deletedItem.id} name={deletedItem.name} />

            <Confirmation show={displayClearConfirmationModal} message="Are you sure you want to delete all audit logs?" onHide={hideClearConfirmationModal} onConfirm={handleClear}></Confirmation>

            <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Log Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table responsive striped bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: "150px" }}>Fields</th>
                                <th>Old Values</th>
                                <th>New Values</th>
                            </tr>
                        </thead>
                        <tbody>
                            {showTableDetails()}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={handleCloseModal}><i className="fas fa-times"></i> Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default AuditLogs